import { useContext, useEffect, useState } from "react";
import { Link, useMatch, useParams, useSearchParams } from "react-router-dom";
import { Tabs } from "antd";
import cn from "classnames";

import { Loading } from "@/shared/components/Loading/Loading";
import { useGetTestHook } from "@/shared/hooks/tests/useGetTestHook";
import { capitalizeFirstLetter } from "@/shared/utils/common.utils";
import { getRoute } from "@/shared/utils/route/route.utils";
import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";

import TableQuestions from "@/modules/tables/TableQuestions";

import buttonStyle from "@/ui-components/commonComponents/Button/style.module.css";

import {
  ADMIN_ROUTE,
  ADD_LANGUAGE_ROUTE,
  COMPETENCIES_ROUTE,
  TEST_INSTRUCTIONS_ROUTE,
  TEST_NORMS_ROUTE,
  TEST_SCALES_ROUTE,
  TESTS_ROUTE,
} from "@/routing/AppRouter/routes.constants";

import { Banner } from "@/ui-components/commonComponents/Banner/Banner";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";

import { TestCompetencies } from "../TestManagement/TestCompetencies";
import { TestNorms } from "../TestManagement/TestNorms/TestNorms";
import TestInstructions from "../TestInstructions/TestInstructions";
import { Scales } from "../Scales/Scales";
import { useScaleTabEligibility } from "../Scales/use-scale-tab-eligibility.hook";
import { TestCreatedModal } from "../CreateTest/TestCreatedModal";

import style from "./style.module.css";

const { TabPane } = Tabs;

const TEST_TABS: Set<string> = new Set([COMPETENCIES_ROUTE, TEST_NORMS_ROUTE, TEST_INSTRUCTIONS_ROUTE]);

export default function TestSettings() {
  const { test_id: testId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const DEFAULT_TEST_SETTINGS_ROUTE = `/${ADMIN_ROUTE}/${TESTS_ROUTE}/${testId}`;
  const { test, isLoading } = useGetTestHook(Number(testId));

  const clientAbility = useContext(ClientAbilityContext);
  const canUpdateTest = clientAbility.can(Action.Update, Subject.TEST);
  const { isEligible: showScalesTab, isLoading: isScaleEligibilityLoading } = useScaleTabEligibility(testId);
  const [isNextStepsModalVisible, setIsNextStepsModalVisible] = useState(false);

  useEffect(() => {
    if (test && searchParams.get("next-steps") === "true") {
      setIsNextStepsModalVisible(true);
      setSearchParams(
        (prev) => {
          prev.delete("next-steps");
          return prev;
        },
        { replace: true },
      );
    }
  }, [test, searchParams, setSearchParams]);

  const tabMatch = useMatch(`/${ADMIN_ROUTE}/${TESTS_ROUTE}/:test_id/:tab`);
  const currentTab = tabMatch?.params.tab;
  const isKnownTab = !!currentTab && (TEST_TABS.has(currentTab) || (showScalesTab && currentTab === TEST_SCALES_ROUTE));
  const activeTab = isKnownTab ? currentTab : "elements";

  if (isLoading || !test || isScaleEligibilityLoading) {
    return <Loading />;
  }

  return (
    <div className="main-container">
      <SEO titleParams={[test.name, capitalizeFirstLetter(activeTab)]} />
      <div className="inner-container">
        <div className={style.header}>
          <div className={style.headerMain}>
            <Banner title={test.name} description={test.internalName} />
          </div>
          {canUpdateTest && (
            <Link
              to={getRoute(DEFAULT_TEST_SETTINGS_ROUTE, ADD_LANGUAGE_ROUTE)}
              className={cn(buttonStyle.linkAsButton, buttonStyle.linkAsButtonPrimary)}
            >
              Add Language
            </Link>
          )}
        </div>
        <div className={style.container}>
          <Tabs activeKey={activeTab}>
            <TabPane
              tab={
                <Link to={getRoute(DEFAULT_TEST_SETTINGS_ROUTE, "")} className={style.tabLink}>
                  Elements
                </Link>
              }
              key="elements"
            >
              <TableQuestions test={test} />
            </TabPane>

            <TabPane
              tab={
                <Link to={getRoute(DEFAULT_TEST_SETTINGS_ROUTE, COMPETENCIES_ROUTE)} className={style.tabLink}>
                  Competencies
                </Link>
              }
              key={COMPETENCIES_ROUTE}
            >
              <TestCompetencies />
            </TabPane>

            {showScalesTab && (
              <TabPane
                tab={
                  <Link to={getRoute(DEFAULT_TEST_SETTINGS_ROUTE, TEST_SCALES_ROUTE)} className={style.tabLink}>
                    Scales
                  </Link>
                }
                key={TEST_SCALES_ROUTE}
              >
                <Scales />
              </TabPane>
            )}

            <TabPane
              tab={
                <Link to={getRoute(DEFAULT_TEST_SETTINGS_ROUTE, TEST_NORMS_ROUTE)} className={style.tabLink}>
                  Norms
                </Link>
              }
              key={TEST_NORMS_ROUTE}
            >
              <TestNorms />
            </TabPane>

            <TabPane
              tab={
                <Link to={getRoute(DEFAULT_TEST_SETTINGS_ROUTE, TEST_INSTRUCTIONS_ROUTE)} className={style.tabLink}>
                  Instructions
                </Link>
              }
              key={TEST_INSTRUCTIONS_ROUTE}
            >
              <TestInstructions test={test} />
            </TabPane>
          </Tabs>
        </div>
      </div>

      <TestCreatedModal
        isVisible={isNextStepsModalVisible}
        testName={test.name}
        internalName={test.internalName}
        onClose={() => setIsNextStepsModalVisible(false)}
      />
    </div>
  );
}
